/* eslint-disable no-unused-vars */
import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import { useDispatch } from "react-redux";
import { makeStyles } from "@material-ui/styles";
import {
  AppBar,
  Button,
  IconButton,
  Toolbar,
  Hidden,
  Avatar,
  colors,
} from "@material-ui/core";
import MenuIcon from "@material-ui/icons/Menu";
import UserPopover from "src/components/User";
import Storage from "src/common/Storage";
import helperUtil from "src/common/utils/helperUtil";
import Image from "src/components/Image";
import { appSettings } from "src/configs";
import { Helmet } from 'react-helmet';

const useStyles = makeStyles((theme) => ({
  root: {
    boxShadow: "none",
    background: `url('${process.env.PUBLIC_URL}/assets/images/nav-bg-1.png') no-repeat left bottom/auto 100%`,
    height: "100%",
    backgroundRepeat: "no-repeat",
    backgroundSize: "100% 100%",
    backgroundPosition: "200px 0",
    backgroundColor: "#eef1f8",
    [theme.breakpoints.down("sm")]: {
      backgroundPosition: "0px 0",
    },
  },
  flexGrow: {
    flexGrow: 1,
  },
  menuButton: {
    marginRight: theme.spacing(1),
  },
  imgLogo: {
    height: 45,
    marginLeft: 310,
    [theme.breakpoints.down("sm")]: {
      marginLeft: 0,
    },
  },
  textLogo: {
    color: "#232a46",
    fontFamily: '"Helvetica 65"',
    fontSize: 24,
    margin: "9px 0 0",
    lineHeight: 1,
    marginLeft: 30,
  },
  boxUserInfo: {
    fontFamily: "helvetica neue",
    color: "#000",
    display: "flex",
    flexDirection: "column",
    textAlign: "right",
  },
  userButton: {
    color: theme.palette.primary.user.color,
    textTransform: "none",
    "& > span > div": {
      marginRight: 8,
    },
  },
  small: {
    border: "3px solid",
    borderColor: theme.palette.primary.user.avatarBorderColor,
    marginRight: 5,
    backgroundColor: "transparent",
  },
  toolbar: {
    minHeight: 100,
    [theme.breakpoints.down("md")]: {
      minHeight: 64,
    },
  },
}));

function TopBar({ onOpenNavBarMobile, className, ...rest }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const userRef = useRef(null);
  const [openUser, setOpenUser] = useState(false);
  const [user, setUser] = useState(undefined);

  useEffect(() => {
    const _user = Storage.getUser();
    if (helperUtil.checkHasObject(_user)) {
      setUser(_user);
    }
  }, []);

  const handleUserOpen = () => {
    setOpenUser(true);
  };

  const handleUserClose = () => {
    setOpenUser(false);
  };

  const user_bu = user?.bu || "";
  const user_role = user?.role || "";
  let userInfoMain = user_bu;
  userInfoMain =
    userInfoMain && user_role ? `${userInfoMain} | ${user_role}` : userInfoMain;

  let userInfo = user?.empcode || "";
  const user_full_name = user?.full_name_en || user?.full_name_th || "";
  const user_position_name =
    user?.postabbr || user?.postname_th || user?.postname_en || "";
  userInfo =
    userInfo && user_full_name ? `${userInfo} | ${user_full_name}` : userInfo;
  userInfo =
    userInfo && user_position_name
      ? `${userInfo} | ${user_position_name}`
      : userInfo;

  return (
    <AppBar {...rest} className={clsx(classes.root, className)} position="static" color="primary">
       <Helmet>
        <title>Gen-AI</title>
      </Helmet>
      <Toolbar className={classes.toolbar}>
        <Hidden lgUp>
          <IconButton
            className={classes.menuButton}
            onClick={onOpenNavBarMobile}
          >
            <MenuIcon />
          </IconButton>
        </Hidden>
        <div className="col-logo">
          <img
            className={classes.imgLogo}
            src="/assets/images/logo.png"
            alt="PTT"
          />
        </div>
        <div>
          <h1 className={classes.textLogo}>PTT MAP Generative AI Plug-in</h1>
        </div>
        <div className={classes.flexGrow} />
        <div className={classes.boxUserInfo}>
          <span style={{ fontWeight: "bold" }}>{userInfoMain}</span>
          <span style={{ fontSize: 12 }}>{userInfo}</span>
        </div>
        <Button
          className={classes.userButton}
          color="inherit"
          onClick={handleUserOpen}
          ref={userRef}
        >
          <Avatar className={classes.small}>
            <Image
              style={{ height: "100%", maxWidth: "100%" }}
              path={helperUtil.getImageProfileUser()}
              alt={user ? user.full_name_en : ""}
              onError={(e) => {
                e.target.src = "/assets/images/user.png";
              }}
            />
          </Avatar>
        </Button>
      </Toolbar>
      <UserPopover
        anchorEl={userRef.current}
        onClose={handleUserClose}
        open={openUser}
      />
    </AppBar>
  );
}

TopBar.propTypes = {
  className: PropTypes.string,
  onOpenNavBarMobile: PropTypes.func,
};

function GenerateAi() {
  const user = Storage.getUser();
  const [receivedData, setReceivedData] = useState({});
  const iframeRef = useRef(null);
  const [isIframeLoaded, setIsIframeLoaded] = useState(false);

useEffect(() => {
  if (window.opener) {
    window.opener.postMessage(
      { type: "IFRAME_READY" },
      window.location.origin
    );
  }

  const saved = sessionStorage.getItem("validationData");
  if (saved) {
    try {
      const parsed = JSON.parse(saved);
      setReceivedData(parsed);
    } catch (err) {
      console.error("Failed to parse saved validationData:", err);
    }
  }

  const handleMessage = (event) => {
    const allowedOrigins = (
      appSettings.appAllowdOrigins || ""
    ).split(",").map(o => o.trim());
    console.log("allowedOrigins: ", allowedOrigins, " event.origin: ",event.origin)
    if (!allowedOrigins.includes(event.origin)) {
      console.log("Blocked message from untrusted origin:", event.origin);
      return;
    }

    if (event.data?.type === "GenerateAi:data" && event.data?.payload) {
      try {
        setReceivedData(event.data.payload);
        sessionStorage.setItem(
          "validationData",
          JSON.stringify(event.data.payload)
        );
      } catch (err) {
        console.error("Failed to save validationData:", err);
      }
    }
  };

  window.addEventListener("message", handleMessage);
  return () => window.removeEventListener("message", handleMessage);
}, []);

  useEffect(() => {
    if (isIframeLoaded && iframeRef.current) {
      iframeRef.current.contentWindow.postMessage(
        {
          type: "VALIDATION_DATA",
          data: {
            validationData: receivedData?.validationData,
            otherData: { userRole: user?.role },
          },
        },
        appSettings.appAIplugin
      );
    }
  }, [isIframeLoaded, receivedData]);

  const handleIframeLoad = () => {
    setIsIframeLoaded(true);
    setTimeout(() => {
      if (iframeRef.current && receivedData?.validationData) {
        iframeRef.current.contentWindow.postMessage(
          {
            type: "VALIDATION_DATA",
            data: {
              validationData: receivedData.validationData,
              otherData: { userRole: user?.role },
            },
          },
          appSettings.appAIplugin
        );
      }
    }, 200);
  };

  return (
    <>
      <TopBar />
      <div>
        <iframe
          ref={iframeRef}
          src={`${appSettings.appAIplugin}/validation-result`}
          style={{ width: "100%", minHeight: "95vh" }}
          title="AI Validation"
          onLoad={handleIframeLoad}
        />
      </div>
    </>
  );
}

export default GenerateAi;